/**
 * Button — sharp-corner button with optional leading / trailing icons.
 *
 * Variants:
 *   `secondary` — paper bg, ink text, line border, hover→ink border
 *   `primary`   — accent bg + accent-ink text (call-to-action)
 *   `ink`       — ink fill, paper text (used by the savebar's submit pair)
 *
 * `mono` flips typography to JetBrains Mono uppercase for editorial / savebar
 * contexts. `size` defaults to medium; `sm` matches Toolband; `lg` matches
 * Savebar.
 *
 * Source: .agents/design/project/artboard-radical.jsx:167-176 (default + coral),
 *         artboard-radical-zakaznik-form.jsx:349-359 (mono savebar pair).
 */

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

export type ButtonVariant = 'secondary' | 'primary' | 'ink';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon node, typically `<Icon size={13} />`. */
  icon?: ReactNode;
  /** Trailing icon node. */
  iconRight?: ReactNode;
  /** JetBrains Mono uppercase typography for editorial moments. */
  mono?: boolean;
  /** Stretch to fill the parent. */
  fullWidth?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

const VARIANT: Record<ButtonVariant, string> = {
  secondary:
    'border-line bg-paper text-ink hover:border-ink disabled:hover:border-line',
  primary:
    'border-accent-fill bg-accent-fill text-accent-ink hover:bg-accent-2 hover:border-accent-2 disabled:hover:bg-accent-fill disabled:hover:border-accent-fill font-semibold',
  ink: 'border-ink bg-ink text-paper hover:bg-accent hover:border-accent disabled:hover:bg-ink disabled:hover:border-ink',
};

// An explicit line box + a minimum height per size: a button with only an icon has no text line, so without them it came
// out shorter than a labelled button beside it (26 vs 31 px for `sm`). `sm` is 30 px like Chip and SegmentedControl.
const SIZE: Record<ButtonSize, string> = {
  sm: 'min-h-[30px] px-3.5 py-1.5 text-[12px] leading-4 gap-1.5',
  md: 'min-h-[36px] px-4 py-2 text-[12.5px] leading-[18px] gap-2',
  lg: 'min-h-[44px] px-[22px] py-3 text-[12px] leading-[18px] gap-2',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  mono = false,
  fullWidth = false,
  className = '',
  children,
  type = 'button',
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={[
        'inline-flex items-center justify-center border font-medium transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        mono ? 'font-mono uppercase tracking-[0.06em]' : 'font-sans',
        SIZE[size],
        VARIANT[variant],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}
